import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import AnimatedMapSprite from "../Maps/logic/AnimimatedMapSprite";

export default function ValentinesShop() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [optimisticFreeClaimed, setOptimisticFreeClaimed] = useState(false);
  const [optimisticHeartDelta, setOptimisticHeartDelta] = useState(0);
  const [claimAnim, setClaimAnim] = useState({ visible: false, up: false });
  const [claimingFree, setClaimingFree] = useState(false);
  const [claimStatus, setClaimStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/v1/valentines/state", { withCredentials: true });
      setState(res.data);
      setOptimisticFreeClaimed(false);
      setOptimisticHeartDelta(0);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load Valentines shop.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const heartBerry = state?.heart_berry_item;
  const heartBerryImg = heartBerry?.image ? `/${heartBerry.image}` : "/Items/heartberry.png";

  const freeClaimed = Boolean(optimisticFreeClaimed || state?.free_claimed);
  const heartBerryOwned = Number(state?.heart_berries ?? 0) + Number(optimisticHeartDelta || 0);

  const canAfford = (cost) => heartBerryOwned >= Number(cost || 0);

  const endsAt = state?.ends_at;

  const endsLabel = useMemo(() => {
    const iso = endsAt;
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }, [endsAt]);

  const formatNumber = (n) => {
    try {
      return new Intl.NumberFormat().format(Number(n) || 0);
    } catch {
      return String(n ?? 0);
    }
  };

  const post = async (path, payload = {}) => {
    setBusy(true);
    setError("");
    try {
      const res = await api.post(path, payload, { withCredentials: true });
      await load();
      return res?.data;
    } catch (e) {
      setError(e.response?.data?.detail || "Action failed.");
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const triggerClaimAnim = () => {
    setClaimAnim({ visible: true, up: false });
    setTimeout(() => setClaimAnim({ visible: true, up: true }), 10);
    setTimeout(() => setClaimAnim({ visible: false, up: false }), 1400);
  };

  const handleClaimFree = async () => {
    if (freeClaimed) {
      setClaimStatus({ kind: "info", text: "Already claimed." });
      return;
    }

    if (claimingFree) {
      setClaimStatus({ kind: "info", text: "Please wait..." });
      return;
    }

    setClaimingFree(true);
    setClaimStatus({ kind: "info", text: "Claiming..." });

    // Optimistic: immediately disable + show animation
    setOptimisticFreeClaimed(true);
    setOptimisticHeartDelta(1);
    triggerClaimAnim();

    try {
      const res = await api.post("/v1/valentines/claim-free", {}, { withCredentials: true });
      const data = res?.data;
      if (data?.already_claimed) {
        // If server says it was already claimed, don't show +1.
        setOptimisticHeartDelta(0);
        setClaimStatus({ kind: "info", text: "Already claimed." });
      } else {
        setState((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            free_claimed: true,
            heart_berries: Number(prev.heart_berries ?? 0) + 1,
          };
        });
        setClaimStatus({ kind: "success", text: "+1 Heart Berry claimed!" });
      }

      // Clear optimistic delta (we've applied it to state) after a moment.
      setTimeout(() => {
        setOptimisticFreeClaimed(false);
        setOptimisticHeartDelta(0);
      }, 600);

      // Sync with backend after the user has time to see feedback.
      setTimeout(() => {
        load();
      }, 900);
    } catch (e) {
      // Revert optimistic UI on failure
      setOptimisticFreeClaimed(false);
      setOptimisticHeartDelta(0);
      setClaimStatus({ kind: "error", text: e?.response?.data?.detail || "Claim failed." });
    } finally {
      setClaimingFree(false);
    }
  };

  const renderCostPill = (amt) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <img
        src={heartBerryImg}
        alt={heartBerry?.name || "Heart Berry"}
        style={{ width: 16, height: 16, flexShrink: 0, imageRendering: "pixelated" }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "/Items/heartberry.png";
        }}
      />
      <span>{formatNumber(amt)} {heartBerry?.name || "Heart Berries"}</span>
    </span>
  );

  const Card = ({ title, children }) => (
    <div
      style={{
        background: "rgba(0, 0, 0, 0.3)",
        padding: 16,
        borderRadius: 12,
        border: "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      <div style={{ fontWeight: 900, marginBottom: 10, fontSize: 14 }}>{title}</div>
      {children}
    </div>
  );

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px 10px",
        boxSizing: "border-box",
        zIndex: 99999,
        pointerEvents: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 920,
          maxHeight: "calc(100vh - 40px)",
          background: "rgba(29, 34, 46, 0.95)",
          color: "#fff",
          borderRadius: 12,
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          pointerEvents: "auto",
        }}
      >
        <div
          style={{
            padding: 20,
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            background: "rgba(0, 0, 0, 0.2)",
            position: "relative",
          }}
        >
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <img
              src="/avatars/177.png"
              alt="Valentines Host"
              style={{ width: 72, height: 72, borderRadius: 10, imageRendering: "pixelated" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>Valentine’s Shop</div>
              <div style={{ opacity: 0.9, marginTop: 6, fontSize: 12, lineHeight: 1.35 }}>
                A limited-time event shop with a roaming promo and fragrance Pokémon.
              </div>
              {endsLabel && (
                <div style={{ opacity: 0.85, marginTop: 6, fontSize: 12 }}>
                  Ends: {endsLabel}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => navigate("/dashboard")}
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              background: "transparent",
              border: "none",
              color: "#fff",
              fontSize: 24,
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: 6,
            }}
            onMouseEnter={(e) => (e.target.style.background = "rgba(255, 255, 255, 0.1)")}
            onMouseLeave={(e) => (e.target.style.background = "transparent")}
          >
            ×
          </button>
        </div>

        <div style={{ flex: 1, padding: 20, overflowY: "auto" }}>
          {error && (
            <div
              style={{
                marginBottom: 16,
                padding: "8px 12px",
                background: "rgba(255,0,0,0.10)",
                color: "#f87171",
                borderRadius: 8,
                textAlign: "center",
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#ccc" }}>Loading…</div>
          ) : !state?.active ? (
            <div style={{ textAlign: "center", padding: 40, color: "#ccc" }}>This event is not active.</div>
          ) : (
            <>
              <Card title="Balance">
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <img
                      src={heartBerryImg}
                      alt={heartBerry?.name || "Heart Berry"}
                      style={{ width: 44, height: 44, imageRendering: "pixelated" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/Items/heartberry.png";
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: 900, fontSize: 14 }}>{heartBerry?.name || "Heart Berry"}</div>
                      <div style={{ opacity: 0.9, fontSize: 12 }}>Owned: {formatNumber(heartBerryOwned)}</div>
                    </div>
                  </div>

                  <div style={{ marginLeft: "auto", position: "relative" }}>
                    <button
                      type="button"
                      disabled={claimingFree || freeClaimed}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void handleClaimFree();
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void handleClaimFree();
                      }}
                      style={{
                        background: freeClaimed ? "rgba(255,255,255,0.10)" : "rgba(236,72,153,0.78)",
                        border: "1px solid rgba(255,255,255,0.14)",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "8px 12px",
                        cursor: claimingFree || freeClaimed ? "not-allowed" : "pointer",
                        minWidth: 200,
                        fontWeight: 900,
                        opacity: claimingFree || freeClaimed ? 0.7 : 1,
                        touchAction: "manipulation",
                      }}
                    >
                      {freeClaimed ? "Free Gift Claimed" : claimingFree ? "Claiming..." : "Claim Free Heart Berry"}
                    </button>

                    {claimAnim.visible && (
                      <div
                        style={{
                          position: "absolute",
                          left: "50%",
                          top: -12,
                          transform: claimAnim.up ? "translate(-50%, -18px)" : "translate(-50%, 0px)",
                          opacity: claimAnim.up ? 0 : 1,
                          transition: "transform 1.2s ease-out, opacity 1.2s ease-out",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          pointerEvents: "none",
                          whiteSpace: "nowrap",
                          fontWeight: 900,
                          fontSize: 12,
                          color: "#4ade80",
                          textShadow: "1px 1px 2px rgba(0,0,0,0.85)",
                        }}
                      >
                        <img
                          src={heartBerryImg}
                          alt=""
                          style={{ width: 16, height: 16, imageRendering: "pixelated" }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/Items/heartberry.png";
                          }}
                        />
                        <span>+1</span>
                      </div>
                    )}

                    {claimStatus && (
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 12,
                          fontWeight: 800,
                          textAlign: "center",
                          color:
                            claimStatus.kind === "success"
                              ? "#4ade80"
                              : claimStatus.kind === "error"
                                ? "#f87171"
                                : "#e5e7eb",
                          opacity: 0.95,
                        }}
                      >
                        {claimStatus.text}
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              <div
                style={{
                  marginTop: 16,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    background: "rgba(0, 0, 0, 0.3)",
                    padding: 16,
                    borderRadius: 12,
                    textAlign: "center",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <h5 style={{ color: "#f1c40f", marginBottom: 12, fontSize: 12 }}>Roaming Promo</h5>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <img
                      src={`/sprites/${state?.map_promo?.pokemon_name}.png`}
                      alt={state?.map_promo?.pokemon_name || "Promo"}
                      width={80}
                      height={80}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ color: "#ccc", fontSize: 10 }}>Map Sprite:</span>
                      <AnimatedMapSprite
                        pokeName={state?.map_promo?.pokemon_name}
                        style={{ width: 32, height: 32 }}
                      />
                    </div>
                  </div>
                  <h6 style={{ color: "#fff", margin: "8px 0", fontSize: 14 }}>
                    {state?.map_promo?.pokemon_name}
                  </h6>
                  <div style={{ color: "#ccc", fontSize: 11, marginBottom: 4 }}>
                    Spawn: {(50 / 4000 * 100).toFixed(2)}%
                  </div>
                  <div style={{ color: "#ccc", fontSize: 11 }}>
                    Caught: {formatNumber(state?.map_promo?.caught ?? 0)}/{formatNumber(state?.map_promo?.limit ?? 0)}
                  </div>
                </div>

                <div
                  style={{
                    background: "rgba(0, 0, 0, 0.3)",
                    padding: 16,
                    borderRadius: 12,
                    textAlign: "center",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <h5 style={{ color: "#a855f7", marginBottom: 12, fontSize: 12 }}>Fragrance</h5>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <img
                      src={`/sprites/${state?.pansage?.pokemon_name}.png`}
                      alt={state?.pansage?.pokemon_name || "Fragrance"}
                      width={80}
                      height={80}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ color: "#ccc", fontSize: 10 }}>Map Sprite:</span>
                      <AnimatedMapSprite
                        pokeName={state?.pansage?.pokemon_name}
                        style={{ width: 32, height: 32 }}
                      />
                    </div>
                  </div>
                  <h6 style={{ color: "#fff", margin: "8px 0", fontSize: 14 }}>
                    {state?.pansage?.pokemon_name}
                  </h6>
                  <div style={{ color: "#ccc", fontSize: 11, marginBottom: 4 }}>
                    Cost: {renderCostPill(state?.pansage?.cost ?? 0)}
                  </div>
                  <div style={{ color: "#ccc", fontSize: 11, marginBottom: 4 }}>
                    Uses granted: {formatNumber(state?.pansage?.uses_per_buy ?? 0)}
                  </div>
                  <div style={{ color: "#ccc", fontSize: 11, marginBottom: 10 }}>
                    Remaining: {formatNumber(state?.pansage?.uses_remaining ?? 0)} | Active: {state?.pansage?.active ? "Yes" : "No"}
                  </div>

                  <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                    <button
                      disabled={
                        busy ||
                        !canAfford(state?.pansage?.cost ?? 0) ||
                        (state?.pansage?.uses_remaining ?? 0) > 0 ||
                        state?.pansage?.active
                      }
                      onClick={() => post("/v1/valentines/buy-fragrance")}
                      style={{
                        background: "rgba(168,85,247,0.78)",
                        border: "1px solid rgba(255,255,255,0.14)",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "8px 12px",
                        cursor:
                          busy ||
                          !canAfford(state?.pansage?.cost ?? 0) ||
                          (state?.pansage?.uses_remaining ?? 0) > 0 ||
                          state?.pansage?.active
                            ? "not-allowed"
                            : "pointer",
                        minWidth: 110,
                        fontWeight: 900,
                        opacity:
                          busy ||
                          !canAfford(state?.pansage?.cost ?? 0) ||
                          (state?.pansage?.uses_remaining ?? 0) > 0 ||
                          state?.pansage?.active
                            ? 0.6
                            : 1,
                      }}
                    >
                      {!canAfford(state?.pansage?.cost ?? 0) ? "Not enough" : "Buy"}
                    </button>
                    <button
                      disabled={busy || (state?.pansage?.uses_remaining ?? 0) <= 0 || state?.pansage?.active}
                      onClick={() => post("/v1/valentines/activate-fragrance")}
                      style={{
                        background: "rgba(34,197,94,0.78)",
                        border: "1px solid rgba(255,255,255,0.14)",
                        color: "#fff",
                        borderRadius: 10,
                        padding: "8px 12px",
                        cursor: "pointer",
                        minWidth: 110,
                        fontWeight: 900,
                        opacity:
                          busy || (state?.pansage?.uses_remaining ?? 0) <= 0 || state?.pansage?.active ? 0.6 : 1,
                      }}
                    >
                      Activate
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    background: "rgba(0, 0, 0, 0.3)",
                    padding: 16,
                    borderRadius: 12,
                    textAlign: "center",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <h5 style={{ color: "#f59e0b", marginBottom: 12, fontSize: 12 }}>Shop Pokémon</h5>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <img
                      src={`/sprites/${state?.cresselia?.pokemon_name}.png`}
                      alt={state?.cresselia?.pokemon_name || "Pokemon"}
                      width={80}
                      height={80}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ color: "#ccc", fontSize: 10 }}>Map Sprite:</span>
                      <AnimatedMapSprite
                        pokeName={state?.cresselia?.pokemon_name}
                        style={{ width: 32, height: 32 }}
                      />
                    </div>
                  </div>
                  <h6 style={{ color: "#fff", margin: "8px 0", fontSize: 14 }}>
                    {state?.cresselia?.pokemon_name}
                  </h6>
                  <div style={{ color: "#ccc", fontSize: 11, marginBottom: 6 }}>
                    Cost: {renderCostPill(state?.cresselia?.cost ?? 0)}
                  </div>
                  <div style={{ color: "#ccc", fontSize: 11, marginBottom: 12 }}>
                    Limit: {formatNumber(state?.cresselia?.limit ?? 1)}
                  </div>

                  <button
                    disabled={busy || state?.cresselia?.purchased || !canAfford(state?.cresselia?.cost ?? 0)}
                    onClick={() => post("/v1/valentines/buy-pokemon", { key: "golden-cresselia" })}
                    style={{
                      background: state?.cresselia?.purchased ? "rgba(255,255,255,0.10)" : "rgba(245,158,11,0.78)",
                      border: "1px solid rgba(255,255,255,0.14)",
                      color: "#fff",
                      borderRadius: 10,
                      padding: "8px 12px",
                      cursor:
                        busy || state?.cresselia?.purchased || !canAfford(state?.cresselia?.cost ?? 0)
                          ? "not-allowed"
                          : "pointer",
                      minWidth: 230,
                      fontWeight: 900,
                      opacity: busy || state?.cresselia?.purchased || !canAfford(state?.cresselia?.cost ?? 0) ? 0.6 : 1,
                    }}
                  >
                    {state?.cresselia?.purchased
                      ? "Purchased"
                      : !canAfford(state?.cresselia?.cost ?? 0)
                        ? "Not enough"
                        : "Buy"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
